<template>
    <div class="phone">
        <div class="messages-wrapper">
            <transition name="fade" mode="out-in">
                <div v-if="!isOptInMessageShown" key="standard" class="messages">
                    <ds-text-button dense class="opt-in-button" @click="showOptInMessage">
                        {{ $options.MESSAGE_CONSTANTS.SHOW_OPTIN_MESSAGE }}
                    </ds-text-button>
                    <span class="date">
                        {{ today }}
                    </span>
                    <span class="message message-preview">
                        {{ messagePreview }}
                    </span>
                </div>

                <div
                    v-else
                    key="opt-in"
                    ref="optInPreview"
                    class="opt-in-message-preview"
                    :class="{ 'hide-scroll-bar': hideScrollBar }"
                >
                    <div class="opt-in-wrapper">
                        <ds-text-button
                            v-if="isHideButtonShown"
                            dense
                            class="opt-in-button shown"
                            @click="hideOptInMessage"
                        >
                            {{ $options.MESSAGE_CONSTANTS.HIDE_OPTIN_MESSAGE }}
                        </ds-text-button>

                        <div v-if="isOptInShown" class="opt-in-message-wrapper">
                            <div class="message opt-in-message" @mouseover="isOptInMessageHovered = true" @mouseleave="isOptInMessageHovered = false">
                                <button class="message-button" @click="isOptInMessageHovered = !isOptInMessageHovered">
                                    {{ optInMessageIntro }}
                                </button>

                                <transition name="fade">
                                    <div
                                        v-if="isOptInMessageHovered"
                                        class="edit-overlay"
                                        @click="isSettingsModalOpen = true"
                                    >
                                        <ds-filled-button leading-icon="edit">
                                            {{ $options.MESSAGE_CONSTANTS.EDIT }}
                                        </ds-filled-button>
                                    </div>
                                </transition>
                            </div>

                            <div class="message-meta-data">
                                {{ formattedPhoneNumber }} {{ $options.MESSAGE_CONSTANTS.OPT_IN_DATE }}
                            </div>
                        </div>

                        <div v-if="isReplyShown" class="opt-in-response-wrapper">
                            <div class="message opt-in-response">
                                {{ $options.MESSAGE_CONSTANTS.YES }}
                            </div>

                            <div class="message-meta-data">
                                {{ $options.MESSAGE_CONSTANTS.RESPONSE_DATE }}
                            </div>
                        </div>

                        <div
                            v-if="isOptInConfirmationShown"
                            ref="confirmationMessage"
                            class="message opt-in-confirmation-message opt-in-message"
                            @mouseover="isOptInConfirmationHovered = true"
                            @mouseleave="isOptInConfirmationHovered = false"
                        >
                            <button class="message-button" @click="isOptInConfirmationHovered = !isOptInConfirmationHovered">
                                {{ optInConfirmationMessage }}
                            </button>

                            <transition name="fade">
                                <div v-if="isOptInConfirmationHovered" class="edit-overlay no-action">
                                    <span class="confirmation-message-description">{{ $options.MESSAGE_CONSTANTS.CONFIRMATION_MESSAGE_DESCRIPTION }}</span>
                                </div>
                            </transition>
                        </div>

                        <div v-show="isMessageShown" ref="message" class="message-wrapper">
                            <div class="message">
                                {{ message }}
                            </div>

                            <div class="message-meta-data">
                                {{ formattedPhoneNumber }} {{ $options.MESSAGE_CONSTANTS.MESSAGE_DATE }}
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
            <div class="input-wrapper">
                <div class="preview-input">
                    <ds-icon class="send-icon" name="send" />
                </div>
            </div>
        </div>

        <sms-broadcast-settings-modal
            :is-open="isSettingsModalOpen"
            @close="closeSettingsModal"
        />
    </div>
</template>

<script>
import moment from 'moment';

import SmsBroadcastSettingsModal from '@/components/broadcast/sms/SmsBroadcastSettingsModal';
import { getSmsAccount } from '@/api/broadcasts/smsBroadcasts';
import { formatPhone } from '@/utils/phone.util';

export const MESSAGE_CONSTANTS = {
    YES: 'Yes',
    EDIT: 'Edit',
    TODAY: 'Today',
    MESSAGE_DATE: 'Just now',
    OPT_IN_DATE: '9:36 am',
    RESPONSE_DATE: '9:37 am',
    SHOW_OPTIN_MESSAGE: 'Show opt-in message',
    HIDE_OPTIN_MESSAGE: 'Hide opt-in message',
    CONFIRMATION_MESSAGE_DESCRIPTION: 'Due to text marketing laws, this content can’t be edited.',
};

const TIME_FORMAT = 'h:mm a';
const DISPLAY_DATE_FORMAT_WITH_YEAR = 'MMM D, YYYY';
const OPT_IN_DELAY = 300;
const REPLY_DELAY = 1300;
const OPT_IN_CONFIRMATION_DELAY = 2300;
const MESSAGE_DELAY = 3300;
const HIDE_BUTTON_DELAY = 4300;

export default {

    MESSAGE_CONSTANTS,
    TIME_FORMAT,
    DISPLAY_DATE_FORMAT_WITH_YEAR,
    formatPhone,

    components: {
        SmsBroadcastSettingsModal,
    },

    props: {
        message: String,
    },

    data () {
        return {
            isSettingsModalOpen: false,
            hideScrollBar: false,
            isOptInMessageShown: false,
            isOptInMessageHovered: false,
            isOptInShown: false,
            isReplyShown: false,
            isOptInConfirmationShown: false,
            isOptInConfirmationHovered: false,
            isMessageShown: false,
            isHideButtonShown: false,
            optInMessageIntro: '',
            phoneNumber: '',
            optInConfirmationMessage: '',
            timeouts: {
                optInTimeout: null,
                replyTimeout: null,
                optInConfirmationTimeout: null,
                messageTimeout: null,
                hideButtonTimeout: null,
            },
        };
    },

    mounted () {
        this.loadAccountInfo();
    },

    computed: {
        today() {
            const formattedDate = moment().format(`${DISPLAY_DATE_FORMAT_WITH_YEAR} ${TIME_FORMAT}`);

            return formattedDate;
        },

        formattedPhoneNumber() {
            return formatPhone(this.phoneNumber);
        },
        messagePreview() {
            return this.message || 'Enter message to see preview...';
        },
    },

    methods: {
        closeSettingsModal() {
            this.isSettingsModalOpen = false;
            this.loadAccountInfo();
        },

        async loadAccountInfo() {
            try {
                const smsAccount = await getSmsAccount();

                this.optInMessageIntro = smsAccount.optInMessageIntro;
                this.phoneNumber = smsAccount.phoneNumber;
                this.optInConfirmationMessage = smsAccount.optInConfirmationMessage;
            } catch (error) {
                this.$error({ message: MESSAGE_CONSTANTS.NO_ACCESS });
            }
        },

        reset() {
            this.clearTimeouts();
            this.hideScrollBar = false;
            this.isOptInShown = false;
            this.isReplyShown = false;
            this.isOptInConfirmationShown = false;
            this.isMessageShown = false;
            this.isHideButtonShown = false;
        },

        clearTimeouts() {
            Object.keys(this.timeouts).forEach((timeout) => {
                clearTimeout(this.timeouts[timeout]);
            });
        },

        showOptInMessage() {
            this.isOptInMessageShown = true;
            this.hideScrollBar = true;
            this.timeouts.optInTimeout = setTimeout(async () => {
                this.isOptInShown = true;
                await this.$nextTick();
                this.$refs.message.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, OPT_IN_DELAY);
            this.timeouts.replyTimeout = setTimeout(() => {
                this.isReplyShown = true;
            }, REPLY_DELAY);
            this.timeouts.optInConfirmationTimeout = setTimeout(async () => {
                this.isOptInConfirmationShown = true;
                await this.$nextTick();
                this.$refs.confirmationMessage.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, OPT_IN_CONFIRMATION_DELAY);
            this.timeouts.messageTimeout = setTimeout(async () => {
                this.isMessageShown = true;
                await this.$nextTick();
                this.$refs.message.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, MESSAGE_DELAY);
            this.timeouts.hideButtonTimeout = setTimeout(() => {
                this.isHideButtonShown = true;
                this.hideScrollBar = false;
            }, HIDE_BUTTON_DELAY);
        },

        hideOptInMessage() {
            this.isOptInMessageShown = false;
            this.reset();
        },
    },
};
</script>

<style lang="scss" scoped>
    div.phone {
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: white;
        width: 18rem;
        height: 35rem;
        border: 3px solid rgba(0, 0, 0, 0.824);
        border-radius: 24px;
        box-shadow: 0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2);
        margin-top: 10px;
    }

    div.phone div.messages-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
        overflow: hidden;
    }

    div.phone div.messages {
        display: flex;
        flex-direction: column;
        flex: 1;
        margin: .5rem 1rem;
    }

    div.phone span.date {
        margin-top: auto;
        padding-bottom: .5rem;
        align-self: center;
        color: $color-ink-700;
        font-size: .75rem;
    }

    div.phone .message {
        word-break: break-word;
        white-space: pre-line;
        background-color: $color-ink-100;
        font-size: 1rem;
        line-height: 1.5rem;
        min-height: 3.125rem;
        max-width: 13.8125rem;
        width: 100%;
        padding: 1rem;
        border-radius: .75rem;
    }

    div.phone .message-button {
        background-color: transparent;
        padding: 0;
        color: inherit;
        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
        text-align: inherit;
    }

    div.phone div.input-wrapper {
        padding: 0 1rem 1rem;
        width: 100%;
    }

    div.phone div.preview-input {
        padding: 0 .375rem;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: reverse;
        -ms-flex-direction: row-reverse;
        flex-direction: row-reverse;
        -webkit-box-align: center;
        -ms-flex-align: center;
        align-items: center;
        height: 2rem;
        width: 100%;
        background-color: $color-gray-050;
        border-radius: .375rem;
        --icon-color: $color-ink-200;
        --icon-size: 1.14562rem;
    }

    div.phone div.preview-input > svg {
        opacity: 0.1;
    }

    div.phone .opt-in-message-preview {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow-y: auto;
        scroll-behavior: smooth;
        padding: 0 $spacing-100 0 $spacing-200;
        margin: $spacing-200 $spacing-025 $spacing-100 0;
    }

    div.phone .opt-in-button {
        align-self: center;
        margin: .5rem  2rem;
    }

    div.phone .shown {
        margin-top: 0;
    }

    div.phone .opt-in-message-wrapper {
        animation: fromBottomLeft .3s ease-in-out 0s;
    }

    div.phone .opt-in-message {
        position: relative;
        overflow: hidden;
    }

    div.phone .opt-in-message .edit-overlay {
        cursor: pointer;
    }

    div.phone div.edit-overlay.no-action {
        cursor: default;
    }

    div.phone .edit-overlay {
        position: absolute;
        background-color: $color-ink-500;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    div.phone .message-meta-data{
        padding: .5rem 0 1rem;
        color: $color-ink-800;
    }

    @keyframes fromBottomLeft {
        0% {
            transform: translate(-100%, 75%);
            opacity: 0;
            visibility: hidden;
        }
        80% {
            transform: translate(-10%, 0);
            opacity: 1;
            visibility: visible;
        }
        100% {
            transform: translate(0, 0);
        }
    }

    @keyframes fromBottomRight {
        0% {
            transform: translate(100%, 75%);
            opacity: 0;
            visibility: hidden;
        }
        80% {
            transform: translate(10%, 0);
            opactiy: 1;
            visibility: visible;
        }
        100% {
            transform: translate(0, 0);
        }
    }

    div.phone .opt-in-response-wrapper {
        display: flex;
        flex-direction: column;
    }

    div.phone .opt-in-response {
        width: auto;
        align-self: flex-end;
        background-color: $color-green-050;
    }

    div.phone .message-wrapper {
        margin-top: .5rem;
    }

    div.phone .message-meta-data {
        align-self: flex-end;
    }

    div.phone .opt-in-response-wrapper {
        animation: fromBottomRight .2s ease-in-out 0s;
    }

    div.phone .opt-in-confirmation-message {
        animation: fromBottomLeft .4s ease-in-out 0s;
    }

    div.phone .confirmation-message-description {
        background-color: white;
        width: 80%;
        padding: .5rem;
        font-size: .8rem;
        border-radius: 8px;
    }

    div.phone .message-wrapper {
        animation: fromBottomLeft .4s ease-in-out 0s;
    }
</style>
